<template>
    <div class="tw-flex tw-flex-wrap">
        <div class="tw-w-full md:tw-w-1/3 lg:tw-w-1/4 tw-p-2 md:tw-pr-6">
            <div class="tw-mb-4" v-if="hasSubAccounts">
                <v-label>
                    <span class="tw-text-sm tw-mb-2 tw-text-gray-500">
                        {{ $t('message.filterByPackageOwner') }}:
                    </span>
                </v-label>
                <v-select
                    :items="accountOptions"
                    v-model="selectedAccount"
                    :item-text="(item) => item.text"
                    :item-value="(item) => item.value"
                    outlined
                    dense
                    @change="onSelectAccount"
                    class="tw-w-full"
                />
            </div>

            <v-card
                elevation="0"
                class="tw-mb-4 tw-overflow-hidden tw-hidden md:tw-block"
                style="max-width: 320px"
            >
                <v-list class="tw-p-0">
                    <v-list-item
                        :active="selectedTab === 0"
                        color="primary"
                        @click="selectedTab = 0"
                        class="tw-cursor-pointer tw-px-4"
                        :style="itemTabStyle(0)"
                    >
                        <v-list-item-title class="tw-flex-1">{{
                            $t('message.singlePurchasePackages')
                        }}</v-list-item-title>
                        <v-icon v-if="selectedTab === 0"
                            >mdi-chevron-right</v-icon
                        >
                    </v-list-item>
                    <v-list-item
                        :active="selectedTab === 1"
                        color="primary"
                        @click="selectedTab = 1"
                        class="tw-cursor-pointer tw-px-4"
                        :style="itemTabStyle(1)"
                    >
                        <v-list-item-title class="tw-flex-1">{{
                            $t('message.packagesRecurring')
                        }}</v-list-item-title>
                        <v-tooltip v-model="tooltip" top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click.stop="tooltip = !tooltip"
                                    small
                                    color="grey"
                                    class="tw-ml-1"
                                    >mdi-help-circle</v-icon
                                >
                            </template>
                            <span>{{
                                $t('message.packagesRecurringDescription')
                            }}</span>
                        </v-tooltip>
                        <v-icon v-if="selectedTab === 1"
                            >mdi-chevron-right</v-icon
                        >
                    </v-list-item>
                </v-list>
            </v-card>

            <div class="tw-block md:tw-hidden">
                <v-tabs
                    class="tw-w-full tw-normal-case tw-font-normal"
                    show-arrows
                    :color="studio.webAppFontColor"
                >
                    <v-tab
                        :active="selectedTab === 0"
                        @click="selectedTab = 0"
                        class="tw-flex-1 tw-normal-case tw-font-normal"
                    >
                        {{ $t('message.singlePurchasePackages') }}
                    </v-tab>
                    <v-tab
                        :active="selectedTab === 1"
                        @click="selectedTab = 1"
                        class="tw-flex-1 tw-normal-case tw-font-normal"
                    >
                        {{ $t('message.packagesRecurring') }}
                    </v-tab>
                </v-tabs>
            </div>
        </div>

        <div class="tw-w-full md:tw-w-2/3 lg:tw-w-3/4">
            <div class="tw-flex tw-flex-wrap">
                <template v-if="selectedTab === 0">
                    <div
                        class="tw-w-full md:tw-w-1/2 tw-p-2 md:tw-min-w-[400px]"
                        v-for="(packageItem, index) in packages"
                        :key="index"
                    >
                        <PackageOwnedCard
                            :packageItem="packageItem"
                            @sharePackage="sharePackage"
                            :subAccounts="subAccounts"
                            @refresh="refresh"
                        />
                    </div>
                    <div
                        class="tw-w-full tw-text-secondary"
                        v-if="packages.length === 0"
                    >
                        {{ $t('message.packagesNotAvailable') }}
                    </div>
                </template>
                <template v-else>
                    <div
                        class="tw-w-full md:tw-w-1/2 tw-p-2 md:tw-min-w-[400px]"
                        v-for="(packageItem, index) in recurringPackages"
                        :key="index"
                    >
                        <PackageOwnedCard
                            :packageItem="packageItem"
                            @sharePackage="sharePackage"
                            :subAccounts="subAccounts"
                            @refresh="refresh"
                        />
                    </div>
                    <div
                        class="tw-w-full tw-text-secondary"
                        v-if="recurringPackages.length === 0"
                    >
                        {{ $t('message.packagesRecurringNotAvailable') }}
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>
<script>
import PackageOwnedCard from '@/components/packages/PackageOwnedCard'
import { mapGetters } from 'vuex'
import { STUDIO_MODULE_NAME } from '@/store/moduleNames'
export default {
    components: {
        PackageOwnedCard,
    },
    props: {
        packages: Array,
        recurringPackages: Array,
        familyAccounts: Array,
    },
    data() {
        return {
            tooltip: false,
            selectedTab: 0,
            selectedAccount: null,
        }
    },
    computed: {
        ...mapGetters({
            studio: `${STUDIO_MODULE_NAME}/getStudio`,
        }),
        activeTabStyle() {
            return {
                backgroundColor: `${this.studio.primaryColor}2e`,
                fontWeight: 'bold',
            }
        },
        hasSubAccounts() {
            return this.familyAccounts.length > 1
        },
        accountOptions() {
            return this.familyAccounts.map((account, index) => ({
                text: this.getAccountLabel(account, index),
                value: account,
            }))
        },
        subAccounts() {
            if (this.hasSubAccounts) {
                return this.familyAccounts.slice(1)
            }
            return []
        },
    },
    watch: {
        familyAccounts(newVal) {
            if (newVal.length > 0) {
                this.selectedAccount = newVal[0]
            }
        },
    },
    methods: {
        itemTabStyle(index) {
            const isActive = index === this.selectedTab
            return isActive ? this.activeTabStyle : {}
        },
        sharePackage() {
            this.$emit('sharePackage')
        },
        getAccountLabel(account, index) {
            if (index === 0) {
                return `${account.fullName} (Myself)`
            } else {
                return account.fullName
            }
        },
        onSelectAccount(account) {
            this.$emit('selectAccount', account.id)
        },
        refresh() {
            this.$emit('refresh', this.selectedAccount?.id)
        },
    },
}
</script>
